import React, { useEffect, useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { PauseCircleFill, PlayCircleFill, XCircleFill } from "react-bootstrap-icons";
import LoadingIndicator from "./LoadingIndicator";

const AudioPlayer = ({ streamDelay, audioUrl, getAudioUrl, initialAudioTime = 0, disabled = false }) => {
    const audioPlayer = useRef();
    const [audioProgress, setAudioProgress] = useState(88);
    const [playing, setPlaying] = useState(false);
    const [audioSource, setAudioSource] = useState(audioUrl);
    const [sourceLoading, setSourceLoading] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [ended, setEnded] = useState(false);
    const [audioError, setAudioError] = useState(false);

    useEffect(() => {
        if (playing && audioSource && !sourceLoading) {
            if (ended) {
                audioPlayer.current.currentTime = initialAudioTime + streamDelay;
                setAudioProgress(88);
                setEnded(false);
            }
            audioPlayer.current.play();
            const interval = setInterval(() => {
                const _elapsed = audioPlayer?.current?.currentTime - streamDelay;
                const progress = 88 * (1 - _elapsed / (audioPlayer.current.duration - streamDelay));
                setAudioProgress(Math.max(0, Math.min(progress, 88)));
            }, 100);
            return () => {
                clearInterval(interval);
            };
        } else {
            audioPlayer.current.pause();
        }
    }, [playing, streamDelay, audioSource, sourceLoading, ended, initialAudioTime]);

    useEffect(() => {
        audioPlayer.current.currentTime = initialAudioTime + streamDelay;
    }, [initialAudioTime, streamDelay]);

    useEffect(() => {
        audioPlayer.current.load();
    }, [audioSource]);

    async function handlePlayClick() {
        if (sourceLoading) {
            return;
        }
        if (audioSource && !audioError) {
            if (!sourceLoading) {
                setPlaying(!playing);
            }
        } else {
            setSourceLoading(true);
            setAudioSource(null);
            try {
                const response = await getAudioUrl();
                setRetryCount(0);
                setAudioSource(response);
            } catch (error) {
                console.error(error);
            }
        }
    }

    function handleCanPlay() {
        setSourceLoading(false);
        setPlaying(true);
        setAudioError(false);
    }

    function handleAudioEnded() {
        setPlaying(false);
        setEnded(true);
    }

    const handleAudioError = event => {
        if (audioSource) {
            const maxRetries = 20;
            if (retryCount < maxRetries) {
                setTimeout(() => {
                    audioPlayer?.current?.load();
                    setRetryCount(retryCount + 1);
                }, 500);
            } else {
                setSourceLoading(false);
                setPlaying(false);
                setAudioError(true);
            }
        }
    };

    return (
        <>
            <Button variant="contained" size="sm" className="p-0 border-0 btn-audio" onClick={handlePlayClick} disabled={disabled}>
                {sourceLoading ? (
                    <div className=" p-2 bg-ra-dark-blue rounded-5">
                        <LoadingIndicator width={16} theme="dark" showText={false} />
                    </div>
                ) : (
                    <svg className="audio-progress">
                        {playing ? (
                            <PauseCircleFill width={32} height={32} />
                        ) : !audioError ? (
                            <PlayCircleFill width={32} height={32} />
                        ) : (
                            <XCircleFill width={32} height={32} />
                        )}
                        <circle cx="16" cy="16" r="14" style={{ strokeDashoffset: audioProgress }}></circle>
                    </svg>
                )}
                <audio ref={audioPlayer} id="player" preload="none" onError={handleAudioError} onCanPlay={handleCanPlay} onEnded={handleAudioEnded}>
                    <source src={audioSource} type="audio/mp3" />
                </audio>
            </Button>
        </>
    );
};

export default AudioPlayer;
